/**
 * Filter Service - Handles cascading filter logic for budget dashboard
 */

// Department mapping for simplified structure
const DEPARTMENT_MAPPING = {
  'IT': ['IT', 'Information Technology'],
  'Admin': ['Admin', 'Administration'],
  'Others': ['Accounts', 'Accounts & Finance', 'HR', 'Human Resources', 'Operations', 'Marketing']
};

// Expense groups available for each department
const DEPARTMENT_EXPENSE_GROUPS = {
  'IT': ['Inventory', 'Non-Inventory'],
  'Admin': ['Inventory', 'Non-Inventory'],
  'Others': ['Inventory', 'Non-Inventory']
};

// Expense types available for each department and expense group
const DEPARTMENT_EXPENSE_TYPES = {
  'IT': {
    'Inventory': ['CAPEX', 'OPEX'],
    'Non-Inventory': ['OPEX']
  },
  'Admin': {
    'Inventory': ['CAPEX', 'OPEX'],
    'Non-Inventory': ['OPEX']
  },
  'Others': {
    'Inventory': ['CAPEX', 'OPEX'],
    'Non-Inventory': ['OPEX']
  }
};

// Categories available for each department
const DEPARTMENT_CATEGORIES = {
  'IT': [
    { id: 'hardware', name: 'Hardware', group: 'Inventory', types: ['CAPEX', 'OPEX'], color: '#3b82f6' },
    { id: 'software', name: 'Software', group: 'Non-Inventory', types: ['OPEX'], color: '#3b82f6' },
    { id: 'networking', name: 'Networking Equipment', group: 'Inventory', types: ['CAPEX'], color: '#3b82f6' },
    { id: 'it-services', name: 'IT Services', group: 'Non-Inventory', types: ['OPEX'], color: '#3b82f6' }
  ],
  'Admin': [
    { id: 'office-supplies', name: 'Office Supplies', group: 'Non-Inventory', types: ['OPEX'], color: '#10b981' },
    { id: 'furniture', name: 'Furniture', group: 'Inventory', types: ['CAPEX', 'OPEX'], color: '#10b981' },
    { id: 'office-equipment', name: 'Office Equipment', group: 'Inventory', types: ['CAPEX'], color: '#10b981' },
    { id: 'utilities', name: 'Utilities', group: 'Non-Inventory', types: ['OPEX'], color: '#10b981' },
    { id: 'maintenance', name: 'Maintenance & Repairs', group: 'Non-Inventory', types: ['OPEX'], color: '#10b981' },
    { id: 'admin-vehicles', name: 'Administrative Vehicles', group: 'Inventory', types: ['CAPEX'], color: '#10b981' }
  ],
  'Others': [
    { id: 'travel', name: 'Travel & Transportation', group: 'Non-Inventory', types: ['OPEX'], color: '#f59e0b' },
    { id: 'training', name: 'Training & Development', group: 'Non-Inventory', types: ['OPEX'], color: '#8b5cf6' },
    { id: 'marketing-materials', name: 'Marketing Materials', group: 'Non-Inventory', types: ['OPEX'], color: '#06b6d4' },
    { id: 'consulting', name: 'Consulting Services', group: 'Non-Inventory', types: ['OPEX'], color: '#ef4444' },
    { id: 'equipment', name: 'Equipment', group: 'Inventory', types: ['CAPEX'], color: '#f59e0b' },
    { id: 'vehicles', name: 'Vehicles', group: 'Inventory', types: ['CAPEX'], color: '#f59e0b' }
  ]
};

class FilterService {
  /**
   * Get available expense groups for selected departments
   */
  static getAvailableExpenseGroups(selectedDepartments = []) {
    if (selectedDepartments.length === 0) {
      return ['Inventory', 'Non-Inventory'];
    }

    const availableGroups = new Set();
    selectedDepartments.forEach(dept => {
      const groups = DEPARTMENT_EXPENSE_GROUPS[dept] || [];
      groups.forEach(group => availableGroups.add(group));
    });

    return Array.from(availableGroups);
  }

  /**
   * Get available expense types for selected departments and expense groups
   */
  static getAvailableExpenseTypes(selectedDepartments = [], selectedExpenseGroups = []) {
    if (selectedDepartments.length === 0) {
      return ['CAPEX', 'OPEX'];
    }

    const availableTypes = new Set();
    
    selectedDepartments.forEach(dept => {
      const deptTypes = DEPARTMENT_EXPENSE_TYPES[dept] || {};
      
      if (selectedExpenseGroups.length === 0) {
        // If no expense groups selected, get all types for this department
        Object.values(deptTypes).forEach(types => {
          types.forEach(type => availableTypes.add(type));
        });
      } else {
        // Get types for selected expense groups
        selectedExpenseGroups.forEach(group => {
          const types = deptTypes[group] || [];
          types.forEach(type => availableTypes.add(type));
        });
      }
    });

    return Array.from(availableTypes);
  }

  /**
   * Get available categories for selected departments, expense groups, and expense types
   */
  static getAvailableCategories(selectedDepartments = [], selectedExpenseGroups = [], selectedExpenseTypes = []) {
    if (selectedDepartments.length === 0) {
      // Return all categories if no departments selected
      return Object.values(DEPARTMENT_CATEGORIES).flat();
    }

    const availableCategories = [];
    
    selectedDepartments.forEach(dept => {
      const deptCategories = DEPARTMENT_CATEGORIES[dept] || [];
      
      deptCategories.forEach(category => {
        // Check if category matches expense group filter
        if (selectedExpenseGroups.length > 0 && !selectedExpenseGroups.includes(category.group)) {
          return;
        }
        
        // Check if category matches expense type filter
        if (selectedExpenseTypes.length > 0) {
          const hasMatchingType = category.types.some(type => selectedExpenseTypes.includes(type));
          if (!hasMatchingType) {
            return;
          }
        }
        
        availableCategories.push(category);
      });
    });

    return availableCategories;
  }

  /**
   * Filter out invalid selections when filters change
   */
  static validateAndCleanFilters(filters) {
    const { departments = [], expenseGroups = [], expenseTypes = [], categories = [] } = filters;
    
    // Get available options based on current selections
    const availableExpenseGroups = this.getAvailableExpenseGroups(departments);
    const availableExpenseTypes = this.getAvailableExpenseTypes(departments, expenseGroups);
    const availableCategories = this.getAvailableCategories(departments, expenseGroups, expenseTypes);
    
    // Clean invalid selections
    const cleanedFilters = {
      ...filters,
      expenseGroups: expenseGroups.filter(group => availableExpenseGroups.includes(group)),
      expenseTypes: expenseTypes.filter(type => availableExpenseTypes.includes(type)),
      categories: categories.filter(catId => 
        availableCategories.some(cat => cat.id === catId)
      )
    };
    
    return cleanedFilters;
  }

  /**
   * Get filter summary for display
   */
  static getFilterSummary(filters) {
    const { departments = [], expenseGroups = [], expenseTypes = [], categories = [] } = filters;
    
    const summary = [];
    
    if (departments.length > 0) {
      summary.push(`${departments.length} department${departments.length > 1 ? 's' : ''}`);
    }
    
    if (expenseGroups.length > 0) {
      summary.push(`${expenseGroups.join(', ')}`);
    }
    
    if (expenseTypes.length > 0) {
      summary.push(`${expenseTypes.join(', ')}`);
    }
    
    if (categories.length > 0) {
      summary.push(`${categories.length} categor${categories.length > 1 ? 'ies' : 'y'}`);
    }
    
    return summary.join(' • ');
  }

  /**
   * Convert simplified departments to original department names for API calls
   */
  static getOriginalDepartmentNames(simplifiedDepartments = []) {
    const originalNames = [];
    
    simplifiedDepartments.forEach(dept => {
      const mappedNames = DEPARTMENT_MAPPING[dept] || [];
      originalNames.push(...mappedNames);
    });
    
    return originalNames;
  }
}

export default FilterService;